import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Chip,
  Tabs,
  Tab,
  Stack,
  Divider,
  CircularProgress,
  Button,
  useTheme,
  useMediaQuery,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Checkbox,
  ListItemText,
  OutlinedInput,
  InputAdornment,
  Snackbar,
  Alert,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  AccessTime as TimeIcon,
  AccessTime as AccessTimeIcon,
  DirectionsCar as CarIcon,
  Build as ServiceIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Schedule as ScheduleIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import ServicesList from '../calendar/ServicesList';
import { PDFDownloadLink, Document, Page, Text, View, StyleSheet, pdf, Image } from '@react-pdf/renderer';
import { getCategoryStyle } from '../../utils/categoryConstants';
import NewAppointmentModal from './NewAppointmentModal';
import CustomerContactEditor from './CustomerContactEditor';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';

// PDF Styles
const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 10,
    paddingTop: 30,
    paddingLeft: 20,
    paddingRight: 20,
    paddingBottom: 30,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  companyInfo: {
    flex: 1,
  },
  companyHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  motIcon: {
    width: 24,
    height: 24,
    marginRight: 8,
  },
  companyName: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  invoiceTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    textAlign: 'right',
  },
  invoiceDetails: {
    textAlign: 'right',
    marginTop: 10,
  },
  section: {
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  customerInfo: {
    marginBottom: 20,
  },
  vehicleInfo: {
    marginBottom: 20,
  },
  servicesTable: {
    marginBottom: 20,
  },
  tableHeader: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#000',
    paddingBottom: 5,
    marginBottom: 10,
  },
  tableRow: {
    flexDirection: 'row',
    marginBottom: 5,
  },
  tableCol1: {
    flex: 3,
  },
  tableCol2: {
    flex: 1,
    textAlign: 'right',
  },
  totalSection: {
    marginLeft: 'auto',
    width: 150,
    borderTopWidth: 1,
    borderTopColor: '#000',
    paddingTop: 10,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 5,
  },
  totalRowBold: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 5,
    fontWeight: 'bold',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 20,
    right: 20,
    textAlign: 'center',
    fontSize: 8,
  },
});

// PDF Document Component
const InvoicePDF = ({ bookingData, appointment, formatDateDisplay, formatTimeDisplay, formatPaymentStatusDisplay }) => {
  const calculateTotal = () => {
    let total = 0;
    bookingData?.products?.forEach(product => {
      total += parseFloat(product.custom_price || 0);
    });
    bookingData?.addons?.forEach(addon => {
      total += parseFloat(addon.custom_price || 0);
    });
    return total;
  };

  const total = calculateTotal();

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.companyInfo}>
            <View style={styles.companyHeader}>
              <Image style={styles.motIcon} src="/images/mot_icon.png" />
              <Text style={styles.companyName}>{bookingData.garage?.name || 'Garage Name'}</Text>
            </View>
            {bookingData.garage?.address && <Text>{bookingData.garage.address}</Text>}
            {bookingData.garage?.postcode && <Text>{bookingData.garage.postcode}</Text>}
            {bookingData.garage?.phone && <Text>Phone: {bookingData.garage.phone}</Text>}
            {bookingData.garage?.email && <Text>Email: {bookingData.garage.email}</Text>}
          </View>
          <View>
            <Text style={styles.invoiceTitle}>INVOICE</Text>
            <View style={styles.invoiceDetails}>
              <Text>Invoice #: {appointment.booking_ref}</Text>
              <Text>{new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/,/g, '')} {new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })}</Text>
            </View>
          </View>
        </View>

        {/* Customer Information */}
        <View style={styles.customerInfo}>
          <Text style={styles.sectionTitle}>Bill To:</Text>
          <Text>{bookingData?.customer?.customer_name || appointment.customer_name || 'Customer Name'}</Text>
          {(bookingData?.customer?.mobile || appointment.phone) && (
            <Text>Phone: {bookingData?.customer?.mobile || appointment.phone}</Text>
          )}
        </View>

        {/* Appointment Information */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Appointment Details:</Text>
          <Text>Date: {formatDateDisplay(appointment.booking_date)}</Text>
          <Text>Time: {formatTimeDisplay(appointment.booking_time)}</Text>
        </View>

        {/* Vehicle Information */}
        {bookingData?.customer?.vehicle && (
          <View style={styles.vehicleInfo}>
            <Text style={styles.sectionTitle}>Vehicle Information:</Text>
            <Text>Registration: {bookingData.customer.vehicle.registration_number}</Text>
            {bookingData.customer.vehicle.make && (
              <Text>Make: {bookingData.customer.vehicle.make}</Text>
            )}
            {bookingData.customer.vehicle.model && (
              <Text>Model: {bookingData.customer.vehicle.model}</Text>
            )}
            {bookingData.customer.vehicle.year && (
              <Text>Year: {bookingData.customer.vehicle.year}</Text>
            )}
            {bookingData.customer.vehicle.colour && (
              <Text>Colour: {bookingData.customer.vehicle.colour}</Text>
            )}
          </View>
        )}

        {/* Services Table */}
        <View style={styles.servicesTable}>
          <Text style={styles.sectionTitle}>Services</Text>
          <View style={styles.tableHeader}>
            <Text style={styles.tableCol1}>Description</Text>
            <Text style={styles.tableCol2}>Amount</Text>
          </View>
          
          {/* Products */}
          {bookingData?.products?.map((product, index) => (
            <View key={index} style={styles.tableRow}>
              <Text style={styles.tableCol1}>{product.name}</Text>
              <Text style={styles.tableCol2}>£{parseFloat(product.custom_price || 0).toFixed(2)}</Text>
            </View>
          ))}
          
          {/* Addons */}
          {bookingData?.addons?.map((addon, index) => (
            <View key={index} style={styles.tableRow}>
              <Text style={styles.tableCol1}>{addon.name} (Add-on)</Text>
              <Text style={styles.tableCol2}>£{parseFloat(addon.custom_price || 0).toFixed(2)}</Text>
            </View>
          ))}
        </View>

        {/* Total Section - Single Line Only */}
        <View style={styles.totalSection}>
          <View style={styles.totalRowBold}>
            <Text>Total:</Text>
            <Text>£{total.toFixed(2)}</Text>
          </View>
        </View>

        {/* Payment Status */}
        <View style={[styles.section, { marginTop: 20 }]}>
          <Text style={styles.sectionTitle}>
            Payment Status: {formatPaymentStatusDisplay(bookingData?.booking?.payment_status || appointment.payment_status)}
          </Text>
        </View>

        {/* Footer */}
        <Text style={styles.footer}>Thank you for your business!</Text>
      </Page>
    </Document>
  );
};

const AppointmentSummaryModal = ({ open, onClose, appointment, onDataChange, availableProducts = [], availableAddons = [], selectedGarage }) => {
  const theme = useTheme();
  const appTheme = useAppTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [currentTab, setCurrentTab] = useState(0);
  const [bookingData, setBookingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updatingPayment, setUpdatingPayment] = useState(false);
  
  // Edit mode states
  const [editedBookingData, setEditedBookingData] = useState(null);
  const [motRefreshStatus, setMotRefreshStatus] = useState(null); // null | 'loading' | 'error'
  const [serviceSearchTerm, setServiceSearchTerm] = useState('');
  const [showAddServicesDialog, setShowAddServicesDialog] = useState(false);
  const [selectedServicesInDialog, setSelectedServicesInDialog] = useState([]);
  const [othersServiceName, setOthersServiceName] = useState('');
  const [othersServicePrice, setOthersServicePrice] = useState('');
  const [savingChanges, setSavingChanges] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [changesMade, setChangesMade] = useState(false);
  const [editingPrices, setEditingPrices] = useState({});
  
  // Notification states
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  
  // Status update states
  const [updatingStatus, setUpdatingStatus] = useState(false);
  
  // Cancel dialog state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancellingAppointment, setCancellingAppointment] = useState(false);
  
  // Reschedule state
  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);

  // Fetch complete booking data when modal opens
  useEffect(() => {
    const fetchBookingData = async () => {
      if (open && appointment?.booking_id) {
        try {
          setLoading(true);
          const response = await apiService.getBooking(appointment.booking_id);
          const data = response.data || response;
          setBookingData(data);
          // Initialize editing data automatically
          setEditedBookingData(JSON.parse(JSON.stringify(data)));
          setHasChanges(false);
        } catch (error) {
          console.error('Error fetching booking details:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchBookingData();
  }, [open, appointment?.booking_id]);

  // Reset manual MOT refresh state when a different appointment is opened
  useEffect(() => {
    setMotRefreshStatus(null);
  }, [appointment?.booking_id]);

  if (!appointment) return null;

  const formatTimeDisplay = (time24) => {
    const [hours, minutes] = time24.split(':').map(Number);
    const hour12 = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
    const ampm = hours >= 12 ? 'pm' : 'am';
    return `${hour12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };

  const formatDateDisplay = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'long',
      day: 'numeric', 
      month: 'long',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'upcoming': return 'primary';
      case 'ongoing': return 'warning';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      case 'no_show': return 'error';
      default: return 'default';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid': return 'success';
      case 'free': return 'info';
      case 'due': return 'warning';
      case 'payment failure': return 'error';
      default: return 'default';
    }
  };

  const formatPaymentStatusDisplay = (status) => {
    if (!status) return '';
    
    switch (status.toLowerCase()) {
      case 'paid':
        return 'Paid';
      case 'due':
        return 'Due';
      case 'free':
        return 'Free';
      case 'payment failure':
        return 'Payment failure';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const formatStatusDisplay = (status) => {
    if (!status) return '';
    
    switch (status.toLowerCase()) {
      case 'no_show':
        return 'No show';
      case 'upcoming':
        return 'Upcoming';
      case 'ongoing':
        return 'Ongoing';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const getStatusOptions = (currentStatus) => {
    const status = currentStatus?.toLowerCase();
    
    switch (status) {
      case 'upcoming':
        return [
          { type: 'status', value: 'ongoing', label: 'Ongoing' },
          { type: 'status', value: 'completed', label: 'Completed' },
          { type: 'status', value: 'no_show', label: 'No show' },
          { type: 'divider' },
          { type: 'action', value: 'reschedule', label: 'Reschedule' },
          { type: 'action', value: 'cancel', label: 'Cancel' }
        ];
      case 'ongoing':
        return [
          { type: 'status', value: 'completed', label: 'Completed' },
          { type: 'status', value: 'no_show', label: 'No show' },
          { type: 'divider' },
          { type: 'action', value: 'reschedule', label: 'Reschedule' },
          { type: 'action', value: 'cancel', label: 'Cancel' }
        ];
      case 'completed':
      case 'cancelled':
      case 'no_show':
        // No options for final states - return empty array to disable dropdown
        return [];
      default:
        return [
          { type: 'status', value: 'ongoing', label: 'Ongoing' },
          { type: 'status', value: 'completed', label: 'Completed' },  
          { type: 'status', value: 'no_show', label: 'No show' }
        ];
    }
  };


  // Customer name/mobile saved on the garage customer record: reflect it here and refresh the calendar on close
  const handleCustomerContactSaved = (updated) => {
    const applyContact = (prev) =>
      prev?.customer ? { ...prev, customer: { ...prev.customer, ...updated } } : prev;
    setBookingData(applyContact);
    setEditedBookingData(applyContact);
    setChangesMade(true);
  };

  // Manually fetch the latest MOT expiry from DVLA
  const handleRefreshMot = async () => {
    const customer = bookingData?.customer;
    if (!customer?.id || motRefreshStatus === 'loading') return;
    setMotRefreshStatus('loading');
    try {
      const response = await apiService.refreshGarageCustomerMot(customer.garage_id, customer.id);
      const result = response.data ?? response;
      if (!result?.checked) {
        setMotRefreshStatus('error');
        return;
      }
      const applyMot = (prev) =>
        prev?.customer?.vehicle
          ? {
              ...prev,
              customer: {
                ...prev.customer,
                vehicle: { ...prev.customer.vehicle, mot_expiry_date: result.mot_expiry_date }
              }
            }
          : prev;
      setBookingData(applyMot);
      setEditedBookingData(applyMot);
      setMotRefreshStatus(null);
    } catch (error) {
      console.error('Error refreshing MOT expiry:', error);
      setMotRefreshStatus('error');
    }
  };

  // Handle adding custom "Others" service
  // Handle service selection in dialog
  const handleServiceToggleInDialog = (service) => {
    setSelectedServicesInDialog(prev => {
      const isSelected = prev.some(s => s.id === service.id);
      if (isSelected) {
        return prev.filter(s => s.id !== service.id);
      } else {
        return [...prev, service];
      }
    });
  };

  const handleRemoveServiceFromDialog = (serviceToRemove) => {
    setSelectedServicesInDialog(prev => prev.filter(service => service.id !== serviceToRemove.id));
  };

  const handleAddSelectedServices = () => {
    selectedServicesInDialog.forEach(service => {
      const isProduct = availableProducts.some(p => p.id === service.id);
      handleAddService(service, isProduct);
    });
    setSelectedServicesInDialog([]);
    setServiceSearchTerm('');
    setShowAddServicesDialog(false);
  };

  const handleAddOthersService = () => {
    if (!othersServiceName.trim() || !othersServicePrice.trim()) {
      return;
    }

    // Find the "Others" product
    const othersProduct = availableProducts.find(p => p.category === 'Others');
    if (!othersProduct) {
      console.error('Others product not found');
      return;
    }

    // Create custom service object
    const customService = {
      ...othersProduct,
      id: `others_${Date.now()}`, // Temporary ID for frontend
      name: othersServiceName,
      custom_name: othersServiceName,
      custom_price: parseFloat(othersServicePrice),
      product_id: othersProduct.product_id || othersProduct.id,
      category: 'Others' // Ensure category is explicitly set
    };

    // Add directly to booking
    handleAddService(customService, true);
    
    // Reset form
    setOthersServiceName('');
    setOthersServicePrice('');
  };

  const handleStatusChange = async (selection) => {
    if (!appointment?.booking_id || updatingStatus) return;
    
    try {
      // If the selection is the same as current status, do nothing
      if (appointment.status?.toLowerCase() === selection?.toLowerCase()) {
        return;
      }
      
      if (selection === 'reschedule') {
        // Open reschedule modal
        setRescheduleModalOpen(true);
        return;
      }
      
      if (selection === 'cancel') {
        // Open cancel dialog to get reason
        setCancelDialogOpen(true);
        return;
      }
      
      setUpdatingStatus(true);
      
      // Call API to update booking status
      await apiService.updateBookingStatus(appointment.booking_id, selection);
      
      // Update status in local appointment data
      appointment.status = selection;
      
      // Update status in booking data if it exists
      if (bookingData) {
        setBookingData(prev => ({
          ...prev,
          booking: {
            ...prev.booking,
            status: selection
          }
        }));
      }
      
      // Update the appointment object in edited data as well
      if (editedBookingData) {
        setEditedBookingData(prev => ({
          ...prev,
          booking: {
            ...prev.booking,
            status: selection
          }
        }));
      }
      
      // Notify parent that changes occurred
      setChangesMade(true);
      
    } catch (error) {
      console.error('Error updating appointment status:', error);
      setSaveError('Failed to update appointment status. Please try again.');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Handle appointment cancellation
  const handleCancelAppointment = async () => {
    if (!appointment?.booking_id || !cancelReason.trim()) {
      setSaveError('Please provide a cancellation reason');
      return;
    }

    try {
      setCancellingAppointment(true);
      
      // Call API to cancel booking with reason
      await apiService.cancelBooking(appointment.booking_id, cancelReason.trim());
      
      // Update status in local appointment data
      appointment.status = 'cancelled';
      
      // Update status in booking data if it exists
      if (bookingData) {
        setBookingData(prev => ({
          ...prev,
          booking: {
            ...prev.booking,
            status: 'cancelled'
          }
        }));
      }
      
      // Update the appointment object in edited data as well
      if (editedBookingData) {
        setEditedBookingData(prev => ({
          ...prev,
          booking: {
            ...prev.booking,
            status: 'cancelled'
          }
        }));
      }
      
      // Close cancel dialog and reset
      setCancelDialogOpen(false);
      setCancelReason('');
      
      // Notify parent that changes occurred
      setChangesMade(true);
      setSaveSuccess(true);
      
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      setSaveError('Failed to cancel appointment. Please try again.');
    } finally {
      setCancellingAppointment(false);
    }
  };

  // Handle reschedule success
  const handleRescheduleSuccess = () => {
    setRescheduleModalOpen(false);
    setChangesMade(true);
    setSaveSuccess(true);
    // Refresh booking data
    if (appointment?.booking_id) {
      const fetchBookingData = async () => {
        try {
          const response = await apiService.getBooking(appointment.booking_id);
          const data = response.data || response;
          setBookingData(data);
          setEditedBookingData(JSON.parse(JSON.stringify(data)));
          
          // Update the appointment object with new date/time from rescheduled booking
          if (data.booking) {
            appointment.booking_date = data.booking.booking_date;
            appointment.booking_time = data.booking.booking_time;
          }
        } catch (error) {
          console.error('Error fetching updated booking details:', error);
        }
      };
      fetchBookingData();
    }
  };

  // Handle reschedule error
  const handleRescheduleError = (errorMessage) => {
    setSaveError(errorMessage || 'Failed to reschedule appointment');
  };

  const handleMarkAsPaid = async () => {
    if (!appointment?.booking_id || updatingPayment) return;
    
    try {
      setUpdatingPayment(true);
      await apiService.updatePaymentStatus(appointment.booking_id, 'paid');
      
      // Update payment status in local state (no need to fetch from server)
      setBookingData(prev => ({
        ...prev,
        booking: {
          ...prev.booking,
          payment_status: 'paid'
        }
      }));
      
      setEditedBookingData(prev => ({
        ...prev,
        booking: {
          ...prev.booking,
          payment_status: 'paid'
        }
      }));
      
      // Notify parent that changes occurred - payment status change doesn't need save button
      setChangesMade(true);
      
    } catch (error) {
      console.error('Error updating payment status:', error);
    } finally {
      setUpdatingPayment(false);
    }
  };

  const handleDownloadInvoice = async () => {
    if (!bookingData || !appointment) return;

    try {
      const blob = await pdf(
        <InvoicePDF
          bookingData={bookingData}
          appointment={appointment}
          formatDateDisplay={formatDateDisplay}
          formatTimeDisplay={formatTimeDisplay}
          formatPaymentStatusDisplay={formatPaymentStatusDisplay}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${appointment.booking_ref}-${appointment.vehicle_regno}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // Reset to original data
  const handleResetChanges = () => {
    if (bookingData) {
      setEditedBookingData(JSON.parse(JSON.stringify(bookingData)));
      setHasChanges(false);
    }
    setServiceSearchTerm('');
    setEditingPrices({});
  };

  // Save changes to booking
  const handleSaveChanges = async () => {
    if (!editedBookingData || !appointment?.booking_id) return;
    
    try {
      setSavingChanges(true);
      
      // Prepare update data
      const updateData = {
        products: editedBookingData.products?.map(p => ({
          product_id: p.product_id || p.id,
          custom_price: p.custom_price || p.default_price,
          custom_name: p.custom_name || undefined
        })) || [],
        addons: editedBookingData.addons?.map(a => ({
          addon_id: a.addon_id || a.id,
          custom_price: a.custom_price || a.default_price,
          custom_name: a.custom_name || undefined
        })) || []
      };
      
      await apiService.updateBooking(appointment.booking_id, updateData);
      
      // Update original booking data with the edited data (no need to fetch from server)
      setBookingData(JSON.parse(JSON.stringify(editedBookingData)));
      
      setHasChanges(false);
      setChangesMade(true);
      setSaveSuccess(true);
      
    } catch (error) {
      console.error('Error updating booking:', error);
      setSaveError('Failed to save changes. Please try again.');
    } finally {
      setSavingChanges(false);
    }
  };

  // Add service (product or addon)
  const handleAddService = (service, isProduct = true) => {
    if (!editedBookingData) return;
    
    const serviceData = {
      ...service,
      id: service.product_id || service.addon_id || service.id,
      product_id: isProduct ? (service.product_id || service.id) : undefined,
      addon_id: !isProduct ? (service.addon_id || service.id) : undefined,
      custom_name: service.custom_name || undefined
    };
    
    setEditedBookingData(prev => ({
      ...prev,
      [isProduct ? 'products' : 'addons']: [
        ...(prev[isProduct ? 'products' : 'addons'] || []),
        serviceData
      ]
    }));
    
    // Always show save changes button when service is added
    setHasChanges(true);
    setChangesMade(true);
  };

  // Remove service
  const handleRemoveService = (serviceId, isProduct = true, customName = null) => {
    if (!editedBookingData) return;
    
    setEditedBookingData(prev => ({
      ...prev,
      [isProduct ? 'products' : 'addons']: (prev[isProduct ? 'products' : 'addons'] || [])
        .filter(item => {
          const itemId = item.id || item.product_id || item.addon_id;
          // For custom services with same product_id, also check custom_name
          if (customName && item.custom_name) {
            return !(itemId === serviceId && item.custom_name === customName);
          }
          // For regular services, just check ID
          return itemId !== serviceId;
        })
    }));
    
    // Always show save changes button when service is removed
    setHasChanges(true);
    setChangesMade(true);
  };

  // Handle price editing
  const handlePriceEdit = (serviceId, isProduct, newPrice) => {
    if (!editedBookingData) return;
    
    const serviceType = isProduct ? 'products' : 'addons';
    
    // Update the edited data
    setEditedBookingData(prev => ({
      ...prev,
      [serviceType]: prev[serviceType].map(item => {
        const itemId = item.id || item.product_id || item.addon_id;
        if (itemId === serviceId) {
          return {
            ...item,
            custom_price: newPrice
          };
        }
        return item;
      })
    }));
    
    // Always show save changes button when price is edited
    setHasChanges(true);
    setChangesMade(true);
  };


  // Toggle price editing mode for a specific service
  const togglePriceEdit = (serviceId, isProduct) => {
    const key = `${serviceId}_${isProduct ? 'product' : 'addon'}`;
    setEditingPrices(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };


  const handleClose = () => {
    // If any changes were made, notify parent to refresh appointments list
    if (changesMade && onDataChange) {
      onDataChange();
    }
    
    // Reset state and close modal
    setEditedBookingData(null);
    setHasChanges(false);
    setChangesMade(false);
    setEditingPrices({});
    setCancelDialogOpen(false);
    setCancelReason('');
    setRescheduleModalOpen(false);
    onClose();
  };

  const calculateTotal = (data = null) => {
    const dataToUse = data || editedBookingData || bookingData;
    if (!dataToUse) return 0;
    let total = 0;
    dataToUse.products?.forEach(product => {
      total += parseFloat(product.custom_price || 0);
    });
    dataToUse.addons?.forEach(addon => {
      total += parseFloat(addon.custom_price || 0);
    });
    return total.toFixed(2);
  };

  // Filter available services (exclude already selected and 'Others' category)
  const getAvailableServices = () => {
    const currentData = editedBookingData || bookingData;
    if (!currentData) return { products: [], addons: [] };
    
    const selectedProductIds = (currentData.products || []).map(p => p.id || p.product_id);
    const selectedAddonIds = (currentData.addons || []).map(a => a.id || a.addon_id);
    
    const filteredProducts = availableProducts.filter(product => 
      !selectedProductIds.includes(product.id) &&
      product.category?.toLowerCase() !== 'others' &&
      (serviceSearchTerm === '' || product.name.toLowerCase().includes(serviceSearchTerm.toLowerCase()))
    );
    
    const filteredAddons = availableAddons.filter(addon => 
      !selectedAddonIds.includes(addon.id) &&
      addon.category?.toLowerCase() !== 'others' &&
      (serviceSearchTerm === '' || addon.name.toLowerCase().includes(serviceSearchTerm.toLowerCase()))
    );
    
    return { products: filteredProducts, addons: filteredAddons };
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, md: 3 },
          m: { xs: 0, md: 2 },
          height: '100%',
          maxHeight: { xs: '100vh', md: '90vh' },
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 2,
        pt: 3,
        px: 3,
        borderBottom: '1px solid',
        borderColor: 'divider',
        flexShrink: 0
      }}>
        <Typography variant="body1" fontWeight="600" sx={{ fontSize: '1.25rem' }}>
          Appointment Details
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ 
        pt: 4, 
        pb: 3,
        px: 3,
        flex: 1, 
        overflow: 'auto' 
      }}>
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            py: 8,
            textAlign: 'center'
          }}>
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Loading appointment details...
            </Typography>
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            {/* Top Section - Date, Time, Customer, Status, Booking Ref */}
            <Box sx={{ 
              p: { xs: 2.5, sm: 3 }, 
              bgcolor: 'background.paper', 
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'primary.main',
              boxShadow: 1,
              mb: 3
            }}>
              {/* Date and Time */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AccessTimeIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant="body1" fontWeight="600" color="primary.main" sx={{ fontSize: '1.25rem' }}>
                  {formatDateDisplay(appointment.booking_date)} at {formatTimeDisplay(appointment.booking_time)}
                </Typography>
              </Box>

              {/* Customer and Phone */}
              <Box sx={{ mb: 2 }}>
                <CustomerContactEditor
                  key={bookingData?.customer?.id || appointment.booking_id}
                  customer={{
                    ...bookingData?.customer,
                    customer_name: bookingData?.customer?.customer_name || appointment.customer_name,
                    mobile: bookingData?.customer?.mobile || appointment.phone,
                  }}
                  editable={!!bookingData?.customer?.id}
                  onSaved={handleCustomerContactSaved}
                  layout="inline"
                  nameTypographyProps={{ variant: 'body1', fontWeight: 500, color: 'text.primary' }}
                />
              </Box>

              {/* Booking Ref and Status */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Ref:
                  </Typography>
                  <Typography variant="body2" color="primary.main" fontWeight="600">
                    {appointment.booking_ref}
                  </Typography>
                </Box>
                
                {/* Status Selector */}
                {(appointment.status?.toLowerCase() === 'completed' || 
                  appointment.status?.toLowerCase() === 'cancelled' || 
                  appointment.status?.toLowerCase() === 'no_show') ? (
                  <Chip
                    label={formatStatusDisplay(appointment.status)}
                    color={getStatusColor(appointment.status)}
                    variant="outlined"
                    size="small"
                    sx={{ fontWeight: 500 }}
                  />
                ) : updatingStatus ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 100, py: 1 }}>
                    <CircularProgress size={16} color="primary" />
                  </Box>
                ) : (
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <Select
                      value=""
                      onChange={(e) => {
                        e.stopPropagation();
                        handleStatusChange(e.target.value);
                      }}
                      disabled={updatingStatus}
                      displayEmpty
                      renderValue={() => formatStatusDisplay(appointment.status)}
                      sx={{
                        '& .MuiOutlinedInput-input': {
                          py: 0.5,
                          fontSize: '0.875rem',
                          color: getStatusColor(appointment.status) === 'success' ? 'success.main' : 
                                 getStatusColor(appointment.status) === 'warning' ? 'warning.main' :
                                 getStatusColor(appointment.status) === 'error' ? 'error.main' : 'primary.main'
                        }
                      }}
                    >
                      {getStatusOptions(appointment.status).map((option, index) => {
                        if (option.type === 'divider') {
                          return <Divider key={index} sx={{ my: 0.5 }} />;
                        }
                        if (option.type === 'action') {
                          return (
                            <MenuItem key={index} value={option.value}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    backgroundColor: option.value === 'reschedule' ? '#1976d2' : '#d32f2f',
                                    flexShrink: 0
                                  }}
                                />
                                {option.label}
                              </Box>
                            </MenuItem>
                          );
                        }
                        return (
                          <MenuItem key={index} value={option.value}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  backgroundColor: getStatusColor(option.value) === 'success' ? '#4caf50' : 
                                                 getStatusColor(option.value) === 'warning' ? '#ff9800' :
                                                 getStatusColor(option.value) === 'error' ? '#f44336' : '#1976d2',
                                  flexShrink: 0
                                }}
                              />
                              {option.label}
                            </Box>
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                )}
              </Box>
            </Box>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs 
                value={currentTab} 
                onChange={handleTabChange}
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '1rem'
                  }
                }}
              >
                <Tab label="Details" />
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>Pricing</span>
                      <Chip
                        label={formatPaymentStatusDisplay(bookingData?.booking?.payment_status || appointment.payment_status)}
                        color={getPaymentStatusColor(bookingData?.booking?.payment_status || appointment.payment_status)}
                        variant="filled"
                        size="small"
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.65rem',
                          height: 18
                        }}
                      />
                    </Box>
                  } 
                />
              </Tabs>
            </Box>

            {/* Tab Content */}
            {currentTab === 0 && (
              <Stack spacing={4}>
                {/* Vehicle Details */}
                {bookingData?.customer?.vehicle && (
                  <Box>
                    <Typography variant="subtitle1" fontWeight="500" color="text.secondary" sx={{ mb: 2, fontSize: '1.125rem' }}>
                      Vehicle
                    </Typography>
                    <Box sx={{ 
                      p: { xs: 2.5, sm: 3 }, 
                      bgcolor: 'background.paper', 
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'primary.main',
                      boxShadow: 1
                    }}>
                      {/* Vehicle Registration & MOT */}
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: { xs: 1, sm: 2 },
                        mb: 2,
                        width: '100%'
                      }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: { xs: 1.5, sm: 2 },
                          flexShrink: 0
                        }}>
                          <CarIcon sx={{ 
                            color: 'primary.main', 
                            fontSize: { xs: 22, sm: 24 },
                            flexShrink: 0
                          }} />
                          <Box sx={{
                            backgroundColor: '#FEDE00',
                            border: '3px solid #000000',
                            borderRadius: 1.5,
                            px: { xs: 1, sm: 2 },
                            py: { xs: 0.5, sm: 0.75 },
                            minWidth: { xs: '70px', sm: '90px' },
                            textAlign: 'center'
                          }}>
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                fontWeight: 900,
                                fontSize: { xs: '0.75rem', sm: '1.1rem' },
                                color: '#000000',
                                letterSpacing: { xs: '0.3px', sm: '1px' },
                                fontFamily: 'monospace',
                                textTransform: 'uppercase',
                                lineHeight: 1.2
                              }}
                            >
                              {bookingData.customer.vehicle.registration_number}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
                          {bookingData.customer.vehicle.mot_expiry_date && (
                            <Chip 
                              label={`MOT expires on ${new Date(bookingData.customer.vehicle.mot_expiry_date).toLocaleDateString('en-GB')}`}
                              color="error"
                              variant="outlined"
                              size="small"
                              sx={{ 
                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                height: { xs: 26, sm: 28 },
                                fontWeight: 500
                              }}
                            />
                          )}
                          <Tooltip
                            title={
                              motRefreshStatus === 'error'
                                ? "Couldn't reach DVLA, showing saved date. Click to retry."
                                : 'Get latest MOT expiry from DVLA'
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                aria-label="Refresh MOT expiry"
                                onClick={handleRefreshMot}
                                disabled={motRefreshStatus === 'loading'}
                                sx={{ p: 0.5, color: motRefreshStatus === 'error' ? 'error.main' : 'text.secondary' }}
                              >
                                {motRefreshStatus === 'loading' ? (
                                  <CircularProgress size={14} />
                                ) : (
                                  <RefreshIcon sx={{ fontSize: 16 }} />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </Box>

                      {/* Vehicle Details */}
                      <Box sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: 1,
                        alignItems: 'center'
                      }}>
                        {[bookingData.customer.vehicle.make, bookingData.customer.vehicle.model, bookingData.customer.vehicle.year, bookingData.customer.vehicle.colour]
                          .filter(Boolean).map((detail, index) => (
                          <Chip
                            key={index}
                            label={detail}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: { xs: '0.7rem', sm: '0.75rem' },
                              height: { xs: 26, sm: 28 },
                              backgroundColor: 'action.selected',
                              borderColor: 'divider'
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Services */}
                <Box>
                  <Typography variant="subtitle1" fontWeight="500" color="text.secondary" sx={{ mb: 2, fontSize: '1.125rem' }}>
                    Services
                  </Typography>
                  <ServicesList 
                    services={[
                      ...(editedBookingData?.products?.map(product => ({
                        name: product.category === 'Others' && product.custom_name ? product.custom_name : product.name,
                        category: product.category
                      })) || []),
                      ...(editedBookingData?.addons?.map(addon => ({
                        name: addon.name,
                        category: addon.category
                      })) || [])
                    ]} 
                    variant="modal"
                  />
                </Box>
              </Stack>
            )}

            {currentTab === 1 && (
              <Stack spacing={3}>
                {/* Services with Pricing */}
                <Box>
                  <Typography variant="subtitle1" fontWeight="500" color="text.secondary" sx={{ mb: 2, fontSize: '1.125rem' }}>
                    Services
                  </Typography>
                  <Stack spacing={1}>
                    {editedBookingData?.products?.map((product, index) => {
                      const productId = product.id || product.product_id;
                      const editKey = `${productId}_product`;
                      const isEditing = editingPrices[editKey];
                      const currentPrice = parseFloat(product.custom_price || 0);
                      
                      return (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            py: 1.5,
                            px: 2,
                            bgcolor: 'background.paper',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                            {(() => {
                              const categoryStyle = getCategoryStyle(product, true);
                              return categoryStyle ? (
                                <Box
                                  sx={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: '50%',
                                    bgcolor: categoryStyle.color,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: '0.7rem',
                                      fontWeight: 600,
                                      color: 'white',
                                      textTransform: 'uppercase',
                                      lineHeight: 1
                                    }}
                                  >
                                    {categoryStyle.letter}
                                  </Typography>
                                </Box>
                              ) : null;
                            })()}
                            <Typography variant="body2" fontWeight="500">
                              {product.category === 'Others' && product.custom_name 
                                ? product.custom_name 
                                : product.name}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                type="number"
                                inputProps={{ step: 0.01, min: 0 }}
                                defaultValue={currentPrice.toFixed(2)}
                                onChange={() => {
                                  // Show save button immediately when user starts typing
                                  setHasChanges(true);
                                }}
                                onBlur={(e) => {
                                  const newPrice = parseFloat(e.target.value) || 0;
                                  handlePriceEdit(productId, true, newPrice.toFixed(2));
                                  togglePriceEdit(productId, true);
                                }}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    e.target.blur();
                                  }
                                }}
                                sx={{ width: { xs: 100, sm: 120, md: 140 } }}
                                InputProps={{
                                  startAdornment: <InputAdornment position="start">£</InputAdornment>
                                }}
                                autoFocus
                              />
                            ) : (
                              <Typography 
                                variant="body1" 
                                fontWeight="600" 
                                color="primary.main"
                                sx={{ cursor: 'pointer', minWidth: { xs: 80, sm: 100, md: 120 }, textAlign: 'right' }}
                                onClick={() => togglePriceEdit(productId, true)}
                              >
                                £{currentPrice.toFixed(2)}
                              </Typography>
                            )}
                            <IconButton
                              size="small"
                              onClick={() => togglePriceEdit(productId, true)}
                              sx={{ color: 'primary.main' }}
                            >
                              <EditIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveService(productId, true, product.custom_name)}
                              sx={{ color: 'error.main' }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                        </Box>
                      );
                    })}
                    {editedBookingData?.addons?.map((addon, index) => {
                      const addonId = addon.id || addon.addon_id;
                      const editKey = `${addonId}_addon`;
                      const isEditing = editingPrices[editKey];
                      const currentPrice = parseFloat(addon.custom_price || 0);
                      
                      return (
                        <Box
                          key={`addon-${index}`}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            py: 1.5,
                            px: 2,
                            bgcolor: 'background.paper',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                            {(() => {
                              const categoryStyle = getCategoryStyle(addon, false);
                              return categoryStyle ? (
                                <Box
                                  sx={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: '50%',
                                    bgcolor: categoryStyle.color,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: '0.7rem',
                                      fontWeight: 600,
                                      color: 'white',
                                      textTransform: 'uppercase',
                                      lineHeight: 1
                                    }}
                                  >
                                    {categoryStyle.letter}
                                  </Typography>
                                </Box>
                              ) : null;
                            })()}
                            <Typography variant="body2" fontWeight="500">
                              {addon.name}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                type="number"
                                inputProps={{ step: 0.01, min: 0 }}
                                defaultValue={currentPrice.toFixed(2)}
                                onChange={() => {
                                  // Show save button immediately when user starts typing
                                  setHasChanges(true);
                                }}
                                onBlur={(e) => {
                                  const newPrice = parseFloat(e.target.value) || 0;
                                  handlePriceEdit(addonId, false, newPrice.toFixed(2));
                                  togglePriceEdit(addonId, false);
                                }}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    e.target.blur();
                                  }
                                }}
                                sx={{ width: { xs: 100, sm: 120, md: 140 } }}
                                InputProps={{
                                  startAdornment: <InputAdornment position="start">£</InputAdornment>
                                }}
                                autoFocus
                              />
                            ) : (
                              <Typography 
                                variant="body1" 
                                fontWeight="600" 
                                color="warning.main"
                                sx={{ cursor: 'pointer', minWidth: { xs: 80, sm: 100, md: 120 }, textAlign: 'right' }}
                                onClick={() => togglePriceEdit(addonId, false)}
                              >
                                £{currentPrice.toFixed(2)}
                              </Typography>
                            )}
                            <IconButton
                              size="small"
                              onClick={() => togglePriceEdit(addonId, false)}
                              sx={{ color: 'warning.main' }}
                            >
                              <EditIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveService(addonId, false, addon.custom_name)}
                              sx={{ color: 'error.main' }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setShowAddServicesDialog(true)}
                      sx={{ 
                        textTransform: 'none',
                        color: 'primary.main',
                        fontWeight: 500
                      }}
                    >
                      Add New Service
                    </Button>
                  </Box>
                </Box>

                {/* Add Custom Service Form */}
                <Box sx={{ mt: 3 }}>
                  <Typography 
                    variant="subtitle1" 
                    fontWeight="500" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 2, 
                      fontSize: { xs: '1rem', sm: '1.125rem' }
                    }}
                  >
                    Add Custom Service
                  </Typography>
                  <Box sx={{ 
                    p: { xs: 1.5, sm: 2 }, 
                    border: '1px solid', 
                    borderColor: appTheme.isDark ? 'rgba(255, 255, 255, 0.23)' : 'divider', 
                    borderRadius: 1,
                    bgcolor: appTheme.isDark ? 'rgba(255, 255, 255, 0.05)' : 'grey.50',
                    transition: 'all 0.2s ease-in-out'
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      gap: { xs: 1, sm: 2 }, 
                      alignItems: 'end',
                      flexDirection: { xs: 'column', sm: 'row' }
                    }}>
                      <TextField
                        label="Service Name"
                        value={othersServiceName}
                        onChange={(e) => setOthersServiceName(e.target.value)}
                        placeholder="Enter custom service name"
                        size="small"
                        fullWidth
                        sx={{ 
                          flex: { sm: 1 },
                          '& .MuiOutlinedInput-root': {
                            bgcolor: appTheme.isDark ? 'rgba(255, 255, 255, 0.05)' : 'background.paper',
                          }
                        }}
                      />
                      <TextField
                        label="Price (£)"
                        value={othersServicePrice}
                        onChange={(e) => setOthersServicePrice(e.target.value)}
                        placeholder="0.00"
                        type="number"
                        inputProps={{ step: 0.01, min: 0 }}
                        size="small"
                        sx={{ 
                          width: { xs: '100%', sm: 120 },
                          '& .MuiOutlinedInput-root': {
                            bgcolor: appTheme.isDark ? 'rgba(255, 255, 255, 0.05)' : 'background.paper',
                          }
                        }}
                      />
                      <Button 
                        onClick={handleAddOthersService}
                        variant="contained"
                        disabled={!othersServiceName.trim() || !othersServicePrice.trim()}
                        size="small"
                        fullWidth={true}
                        sx={{ 
                          height: { xs: 36, sm: 40 },
                          minWidth: { xs: 'auto', sm: 80 },
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          width: { sm: 'auto' },
                          bgcolor: appTheme.isDark ? 'primary.dark' : 'primary.main',
                          '&:hover': {
                            bgcolor: appTheme.isDark ? 'primary.main' : 'primary.dark',
                          }
                        }}
                      >
                        Add
                      </Button>
                    </Box>
                    <Typography 
                      variant="caption" 
                      color={appTheme.isDark ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary'} 
                      sx={{ 
                        mt: 1, 
                        display: 'block',
                        fontSize: { xs: '0.7rem', sm: '0.75rem' }
                      }}
                    >
                      Add custom services that aren't in the standard list
                    </Typography>
                  </Box>
                </Box>

                {/* Total */}
                <Divider />
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  p: 2,
                  bgcolor: 'primary.50',
                  borderRadius: 2
                }}>
                  <Typography variant="body1" fontWeight="600" sx={{ fontSize: '1.25rem' }}>
                    Total
                  </Typography>
                  <Typography variant="body1" fontWeight="600" color="primary.main" sx={{ fontSize: '1.25rem' }}>
                    £{calculateTotal()}
                  </Typography>
                </Box>
              </Stack>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Action Buttons - Show on all tabs */}
      <Box sx={{ 
        p: { xs: 2, sm: 3 },
        pt: { xs: 1.5, sm: 2 },
        borderTop: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        justifyContent: { xs: 'center', sm: 'flex-end' },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 1.5, sm: 1 },
        flexShrink: 0
      }}>
        {/* Show Save Changes when there are changes - hide other buttons */}
        {hasChanges ? (
          <Button
            variant="contained"
            onClick={handleSaveChanges}
            startIcon={<SaveIcon />}
            disabled={savingChanges}
            fullWidth={true}
            size={isMobile ? 'medium' : 'medium'}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              minWidth: { xs: 'auto', sm: 120 },
              fontSize: { xs: '0.875rem', sm: '1rem' },
              py: { xs: 1, sm: 0.75 },
              width: { sm: 'auto' }
            }}
          >
            {savingChanges ? (
              <CircularProgress size={16} sx={{ mr: 1, color: 'inherit' }} />
            ) : null}
            {savingChanges ? 'Saving...' : 'Save Changes'}
          </Button>
        ) : (
          <>
            {/* Show Download and Mark as Paid buttons only when no unsaved changes */}
            <Button
              variant="outlined"
              onClick={handleDownloadInvoice}
              fullWidth={true}
              size={isMobile ? 'medium' : 'medium'}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                minWidth: { xs: 'auto', sm: 120 },
                fontSize: { xs: '0.875rem', sm: '1rem' },
                py: { xs: 1, sm: 0.75 },
                width: { sm: 'auto' }
              }}
            >
              Download Invoice
            </Button>
            
            {(bookingData?.booking?.payment_status || appointment.payment_status)?.toLowerCase() !== 'paid' && (
              <Button
                variant="contained"
                onClick={handleMarkAsPaid}
                disabled={updatingPayment}
                fullWidth={true}
                size={isMobile ? 'medium' : 'medium'}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  minWidth: { xs: 'auto', sm: 120 },
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  py: { xs: 1, sm: 0.75 },
                  width: { sm: 'auto' }
                }}
              >
                {updatingPayment ? (
                  <CircularProgress size={16} sx={{ mr: 1, color: 'inherit' }} />
                ) : null}
                {updatingPayment ? 'Updating...' : 'Mark as Paid'}
              </Button>
            )}
          </>
        )}
      </Box>


      {/* Add Services Dialog */}
      <Dialog 
        open={showAddServicesDialog} 
        onClose={() => setShowAddServicesDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { 
            height: '90vh',
            maxHeight: 800
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0
        }}>
          <Typography variant="body1" fontWeight="600" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
            Add New Services
          </Typography>
          <IconButton onClick={() => setShowAddServicesDialog(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Selected Services Display */}
          {selectedServicesInDialog.length > 0 && (
            <Box sx={{ p: { xs: 1.5, sm: 2 }, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  {selectedServicesInDialog.length} selected
                </Typography>
                <Button
                  size="small"
                  onClick={() => {
                    setSelectedServicesInDialog([]);
                    setServiceSearchTerm('');
                  }}
                  sx={{ 
                    textTransform: 'none',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    minWidth: { xs: 'auto', sm: 64 },
                    px: { xs: 1, sm: 2 }
                  }}
                >
                  Clear All
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 0.25, sm: 0.5 } }}>
                {selectedServicesInDialog.map((service) => {
                  const isProduct = availableProducts.some(p => p.id === service.id);
                  return (
                    <Chip
                      key={service.id}
                      label={service.name}
                      size="small"
                      color={isProduct ? 'primary' : 'success'}
                      onDelete={() => handleRemoveServiceFromDialog(service)}
                      sx={{
                        height: { xs: 24, sm: 28 },
                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                        maxWidth: { xs: '150px', sm: '200px' },
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          px: { xs: 0.5, sm: 1 }
                        },
                        '& .MuiChip-deleteIcon': {
                          fontSize: { xs: '14px', sm: '16px' }
                        }
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Search Bar */}
          <Box sx={{ p: { xs: 1.5, sm: 2 }, borderBottom: '1px solid', borderColor: 'divider' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter to Search"
              value={serviceSearchTerm}
              onChange={(e) => setServiceSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }
              }}
            />
          </Box>

          {/* Services List */}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {getAvailableServices().products.length === 0 && getAvailableServices().addons.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  {serviceSearchTerm ? 'No services found matching your search' : 'All available services have been added'}
                </Typography>
              </Box>
            ) : (
              [...getAvailableServices().products, ...getAvailableServices().addons].map((service) => {
                const isProduct = getAvailableServices().products.some(p => p.id === service.id);
                const isSelected = selectedServicesInDialog.some(s => s.id === service.id);
                const categoryStyle = getCategoryStyle(service);
                
                return (
                  <Box key={service.id}>
                    <Box
                      sx={{
                        p: { xs: 1.5, sm: 2.5 },
                        cursor: 'pointer',
                        bgcolor: isSelected
                          ? isProduct
                            ? 'primary.50'
                            : 'success.50'
                          : 'transparent',
                        '&:hover': {
                          bgcolor: isSelected
                            ? isProduct
                              ? 'primary.100'
                              : 'success.100'
                            : 'action.hover',
                        },
                        transition: 'background-color 0.2s ease',
                      }}
                      onClick={() => handleServiceToggleInDialog(service)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
                        {/* Category Legend Style */}
                        {categoryStyle && (
                          <Box
                            sx={{
                              width: { xs: 20, sm: 24 },
                              height: { xs: 20, sm: 24 },
                              borderRadius: '50%',
                              bgcolor: categoryStyle.color,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                fontWeight: 600,
                                color: 'white',
                                textTransform: 'uppercase',
                                lineHeight: 1
                              }}
                            >
                              {categoryStyle.letter}
                            </Typography>
                          </Box>
                        )}

                        {/* Service Details */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography 
                            variant="body2" 
                            fontWeight="600" 
                            sx={{ 
                              mb: 0.5,
                              fontSize: { xs: '0.875rem', sm: '1rem' }
                            }}
                          >
                            {service.name}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: { xs: 1, sm: 1.5 },
                              flexWrap: 'wrap',
                            }}
                          >
                            <Typography
                              variant="caption"
                              color={service.is_free ? 'success.main' : 'primary.main'}
                              fontWeight="600"
                              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                            >
                              {service.is_free
                                ? 'Free'
                                : `£${service.custom_price || service.default_price}`}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                            >
                              {service.custom_duration || service.duration_minutes || 0} mins
                            </Typography>
                          </Box>
                        </Box>

                        {/* Checkbox */}
                        <Box onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleServiceToggleInDialog(service)}
                            size="small"
                            sx={{
                              color: isProduct ? 'primary.main' : 'success.main',
                              '&.Mui-checked': {
                                color: isProduct ? 'primary.main' : 'success.main',
                              },
                              p: { xs: 0.5, sm: 1 }
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: { xs: 1.5, sm: 2 }, 
          borderTop: '1px solid', 
          borderColor: 'divider',
          gap: { xs: 1, sm: 2 }
        }}>
          <Button 
            onClick={() => setShowAddServicesDialog(false)}
            sx={{ 
              fontSize: { xs: '0.875rem', sm: '1rem' },
              px: { xs: 2, sm: 3 }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddSelectedServices}
            variant="contained"
            disabled={selectedServicesInDialog.length === 0}
            sx={{ 
              fontSize: { xs: '0.875rem', sm: '1rem' },
              px: { xs: 2, sm: 3 }
            }}
          >
            Add {selectedServicesInDialog.length} Service{selectedServicesInDialog.length !== 1 ? 's' : ''}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Notification */}
      <Snackbar
        open={saveSuccess}
        autoHideDuration={4000}
        onClose={() => setSaveSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity="success" 
          onClose={() => setSaveSuccess(false)}
          variant="filled"
          sx={{ width: '100%' }}
        >
          Changes saved successfully!
        </Alert>
      </Snackbar>

      {/* Error Notification */}
      <Snackbar
        open={!!saveError}
        autoHideDuration={6000}
        onClose={() => setSaveError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity="error" 
          onClose={() => setSaveError(null)}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {saveError}
        </Alert>
      </Snackbar>

      {/* Cancel Appointment Dialog */}
      <Dialog 
        open={cancelDialogOpen} 
        onClose={() => {
          setCancelDialogOpen(false);
          setCancelReason('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Appointment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please provide a reason for cancelling this appointment:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Enter cancellation reason..."
            variant="outlined"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setCancelDialogOpen(false);
              setCancelReason('');
            }}
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCancelAppointment}
            color="error"
            variant="contained"
            disabled={!cancelReason.trim() || cancellingAppointment}
          >
            {cancellingAppointment ? 'Cancelling...' : 'Cancel Appointment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reschedule Appointment Modal */}
      {rescheduleModalOpen && (
        <NewAppointmentModal
          open={rescheduleModalOpen}
          onClose={() => setRescheduleModalOpen(false)}
          garageId={selectedGarage?.id || bookingData?.garage?.id}
          onSuccess={handleRescheduleSuccess}
          onError={handleRescheduleError}
          availableProducts={availableProducts}
          availableAddons={availableAddons}
          rescheduleMode={true}
          rescheduleBookingId={appointment?.booking_id}
        />
      )}
    </Dialog>
  );
};

export default AppointmentSummaryModal;